import {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {createAsyncAction} from '.'
import {getAsynCacheSelector} from './selectors'

const asynEqual = (pre, curr) =>
    pre.isLoading === curr.isLoading

function useAsync({
                      query = {},
                      values,
                      deps = [],
                      apiInfo
                  }) {
    // Stabilize query/values by content so inline object literals
    // (e.g. bindQueryParams(...)) don't recreate handleAsyncAction every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableQuery = useMemo(() => query, [JSON.stringify(query)])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stableValues = useMemo(() => values, [JSON.stringify(values)])

    const [
        action = {},
        setAction
    ] = useState(() => {
        const action = createAsyncAction({
            query: stableQuery,
            apiInfo
        })
        return action
    })

    const selector = useMemo(() => {
        return state =>
            getAsynCacheSelector(
                state,
                action.asyncId
            )
    }, [action.asyncId])
    const {
        isLoading = true,
        success,
        errorMessages,
        result,
        time,
        error,
        ...rest
    } = useSelector(selector, asynEqual)
    const dispatch = useDispatch()
    const handleAsyncAction = useCallback(
        (requiredValues = {}) => {
            const action = createAsyncAction({
                query: stableQuery,
                apiInfo,
                values: stableValues,
                ...requiredValues
            })
            setAction(action)
            dispatch(action)
            return action
        },
        [apiInfo, dispatch, stableQuery, stableValues]
    )
    useEffect(() => {
        if ((!!deps?.length && deps.every(item => item !== null && item !== undefined)) || (action && !success)) {
            handleAsyncAction()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps])

    const asyncApi = useMemo(
        () => ({
            action,
            isLoading,
            success,
            errorMessages,
            result,
            error,
            time,
            ...rest,
            handleAsyncAction
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isLoading, handleAsyncAction]
    )

    return asyncApi
}

export default useAsync
